export const DEFAULT_AVATAR_COUNT = 160;
export const DEFAULT_AVATAR_BASE_PATH = "default-avatars";
export const DEFAULT_AVATAR_API_PATH = "/api/default-avatars";
export const DEFAULT_AVATAR_WIDTH = 512;
export const DEFAULT_AVATAR_HEIGHT = 512;
export const DEFAULT_AVATAR_FORMAT = "png";
export const DEFAULT_AVATAR_ASSET_VERSION = "2026-05-23-face";
export const CRIBNOSH_DEFAULT_AVATAR_ID = "avatar-default";
export const CRIBNOSH_DEFAULT_AVATAR_FILE_NAME = "avatar-default.png";
export const CRIBNOSH_DEFAULT_AVATAR_LABEL = "Cribnosh avatar";
export const DEFAULT_AVATAR_CDN_BASE_URL =
  "https://cribnosh-media-production-fjb8a7cjffcaekhd.z03.azurefd.net/convex-storage/default-avatars";

export type CribnoshAvatarId = `avatar-${string}`;

export type CribnoshAvatar = {
  id: CribnoshAvatarId;
  label: string;
  fileName: string;
  path: string;
};

export type CribnoshAvatarResolutionInput = {
  userId?: string | null;
  profileImageUrl?: string | null;
  avatarType?: "uploaded" | "cribnosh" | null;
  cribnoshAvatarId?: string | null;
  avatarBaseUrl: string;
};

export type CribnoshAvatarResolution = {
  kind: "uploaded" | "cribnosh";
  uri: string;
  avatarId?: CribnoshAvatarId;
  label: string;
};

export type CribnoshAvatarPublicManifest = {
  version: 1;
  count: number;
  basePath: string;
  format: typeof DEFAULT_AVATAR_FORMAT;
  width: typeof DEFAULT_AVATAR_WIDTH;
  height: typeof DEFAULT_AVATAR_HEIGHT;
  defaultAvatar: {
    id: typeof CRIBNOSH_DEFAULT_AVATAR_ID;
    label: typeof CRIBNOSH_DEFAULT_AVATAR_LABEL;
    fileName: typeof CRIBNOSH_DEFAULT_AVATAR_FILE_NAME;
    path: string;
  };
  avatars: CribnoshAvatar[];
};

export type CribnoshAvatarBaseUrlEnv = Record<string, string | undefined>;

const DEFAULT_AVATAR_ID_PATTERN = /^avatar-(\d{3})$/;
const UPLOADED_AVATAR_LABEL = "Current profile avatar";
const DEFAULT_WEB_ORIGIN = "https://cribnosh.com";
const PROFILE_AVATAR_URL_FIELD_CANDIDATES = [
  "avatar_url",
  "avatarUrl",
  "profilePicture",
  "profile_picture",
  "picture",
  "profileImageUrl",
  "profile_image_url",
  "profileImage",
  "profile_image",
  "imageUrl",
  "image_url",
  "image",
  "photo",
  "photoURL",
  "photo_url",
] as const;

const USER_AVATAR_URL_FIELD_CANDIDATES = [
  "avatar_url",
  "avatarUrl",
  "profilePicture",
  "profile_picture",
  "picture",
  "imageUrl",
  "image_url",
  "image",
  "photo",
  "photoURL",
  "photo_url",
] as const;

const FOOD_CREATOR_AVATAR_URL_FIELD_CANDIDATES = [
  "foodCreator_profile_image",
  "foodCreatorProfileImage",
  "profileImageUrl",
  "profile_image_url",
  "profileImage",
  "profile_image",
  "avatar_url",
  "avatarUrl",
  "avatar",
  "picture",
  "imageUrl",
  "image_url",
  "image",
  "photo",
  "photoURL",
  "photo_url",
] as const;

function avatarNumberToId(number: number): CribnoshAvatarId {
  return `avatar-${String(number).padStart(3, "0")}`;
}

function avatarIdToNumber(avatarId: string): number | null {
  const match = DEFAULT_AVATAR_ID_PATTERN.exec(avatarId);
  if (!match) return null;

  const number = Number.parseInt(match[1] ?? "", 10);
  if (!Number.isInteger(number) || number < 1 || number > DEFAULT_AVATAR_COUNT) {
    return null;
  }

  return number;
}

function trimSlashes(value: string): string {
  return value.replace(/^\/+|\/+$/g, "");
}

function trimTrailingSlashes(value: string): string {
  return value.replace(/\/+$/g, "");
}

function firstNonEmpty(values: Array<string | undefined>): string | null {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }

  return null;
}

function originFromUrl(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

export const DEFAULT_AVATAR_IDS: CribnoshAvatarId[] = Array.from(
  { length: DEFAULT_AVATAR_COUNT },
  (_, index) => avatarNumberToId(index + 1),
);

export const DEFAULT_AVATARS: CribnoshAvatar[] = DEFAULT_AVATAR_IDS.map((id) => {
  const number = avatarIdToNumber(id) ?? 1;
  const paddedNumber = String(number).padStart(3, "0");
  const fileName = `${id}.png`;

  return {
    id,
    label: `Cribnosh avatar ${paddedNumber}`,
    fileName,
    path: `${DEFAULT_AVATAR_BASE_PATH}/${fileName}`,
  };
});

const DEFAULT_AVATAR_BY_ID = new Map(DEFAULT_AVATARS.map((avatar) => [avatar.id, avatar]));
export const CRIBNOSH_DEFAULT_AVATAR = {
  id: CRIBNOSH_DEFAULT_AVATAR_ID,
  label: CRIBNOSH_DEFAULT_AVATAR_LABEL,
  fileName: CRIBNOSH_DEFAULT_AVATAR_FILE_NAME,
  path: `${DEFAULT_AVATAR_BASE_PATH}/${CRIBNOSH_DEFAULT_AVATAR_FILE_NAME}`,
} as const;

export function normalizeCribnoshAvatarId(value?: string | null): CribnoshAvatarId | null {
  if (typeof value !== "string") return null;

  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;

  const number = avatarIdToNumber(normalized);
  return number ? avatarNumberToId(number) : null;
}

export function getCribnoshAvatarById(value?: string | null): CribnoshAvatar | null {
  const avatarId = normalizeCribnoshAvatarId(value);
  return avatarId ? DEFAULT_AVATAR_BY_ID.get(avatarId) ?? null : null;
}

export function getCribnoshAvatarLabel(value?: string | null): string {
  return getCribnoshAvatarById(value)?.label ?? "Cribnosh avatar";
}

export function getCribnoshAvatarPath(value?: string | null): string {
  const avatar = getCribnoshAvatarById(value) ?? DEFAULT_AVATARS[0];
  return avatar.path;
}

function buildCribnoshAvatarFileUrl(fileName: string, avatarBaseUrl: string): string {
  const trimmedBaseUrl = trimTrailingSlashes(avatarBaseUrl.trim());
  const cacheVersion = `v=${encodeURIComponent(DEFAULT_AVATAR_ASSET_VERSION)}`;

  if (!trimmedBaseUrl) {
    return `/${DEFAULT_AVATAR_BASE_PATH}/${fileName}?${cacheVersion}`;
  }

  const baseAlreadyIncludesAssetPath = trimmedBaseUrl.endsWith(`/${DEFAULT_AVATAR_BASE_PATH}`);
  const filePath = baseAlreadyIncludesAssetPath
    ? fileName
    : `${DEFAULT_AVATAR_BASE_PATH}/${fileName}`;

  return `${trimmedBaseUrl}/${filePath}?${cacheVersion}`;
}

export function getCribnoshAvatarUrl(value: string | null | undefined, avatarBaseUrl: string): string {
  const avatar = getCribnoshAvatarById(value) ?? DEFAULT_AVATARS[0];
  return buildCribnoshAvatarFileUrl(avatar.fileName, avatarBaseUrl);
}

export function getCribnoshDefaultAvatarUrl(avatarBaseUrl: string): string {
  return buildCribnoshAvatarFileUrl(CRIBNOSH_DEFAULT_AVATAR_FILE_NAME, avatarBaseUrl);
}

export function getCribnoshAvatarApiUrl(value: string | null | undefined, origin: string): string {
  const avatar = getCribnoshAvatarById(value) ?? DEFAULT_AVATARS[0];
  const trimmedOrigin = trimTrailingSlashes(origin.trim());
  const apiPath = trimSlashes(DEFAULT_AVATAR_API_PATH);
  const cacheVersion = `v=${encodeURIComponent(DEFAULT_AVATAR_ASSET_VERSION)}`;

  return trimmedOrigin
    ? `${trimmedOrigin}/${apiPath}/${avatar.fileName}?${cacheVersion}`
    : `/${apiPath}/${avatar.fileName}?${cacheVersion}`;
}

export function resolveCribnoshAvatarBaseUrl(
  env: CribnoshAvatarBaseUrlEnv,
  fallbackOrigin = DEFAULT_WEB_ORIGIN,
): string {
  const explicitAvatarBase = firstNonEmpty([
    env.EXPO_PUBLIC_CRIBNOSH_AVATAR_BASE_URL,
    env.NEXT_PUBLIC_CRIBNOSH_AVATAR_BASE_URL,
    env.CRIBNOSH_AVATAR_BASE_URL,
  ]);
  if (explicitAvatarBase) {
    return trimTrailingSlashes(explicitAvatarBase);
  }

  if (fallbackOrigin === DEFAULT_WEB_ORIGIN) {
    return DEFAULT_AVATAR_CDN_BASE_URL;
  }

  const apiBase = firstNonEmpty([
    env.EXPO_PUBLIC_API_BASE_URL,
    env.NEXT_PUBLIC_BASE_URL,
    env.NEXT_PUBLIC_SITE_URL,
  ]);
  const origin = (apiBase ? originFromUrl(apiBase) : null) ?? trimTrailingSlashes(fallbackOrigin);

  return `${origin}${DEFAULT_AVATAR_API_PATH}`;
}

export function getCribnoshAvatarPublicManifest(): CribnoshAvatarPublicManifest {
  return {
    version: 1,
    count: DEFAULT_AVATAR_COUNT,
    basePath: DEFAULT_AVATAR_BASE_PATH,
    format: DEFAULT_AVATAR_FORMAT,
    width: DEFAULT_AVATAR_WIDTH,
    height: DEFAULT_AVATAR_HEIGHT,
    defaultAvatar: CRIBNOSH_DEFAULT_AVATAR,
    avatars: DEFAULT_AVATARS,
  };
}

export function resolveStoredProfileAvatarUrl(
  profile?: Record<string, unknown> | null,
  avatarBaseUrl?: string | null,
): string | undefined {
  return resolveStoredAvatarUrlWithCandidates(
    profile,
    PROFILE_AVATAR_URL_FIELD_CANDIDATES,
    avatarBaseUrl,
  );
}

export function resolveStoredUserAvatarUrl(
  profile?: Record<string, unknown> | null,
  avatarBaseUrl?: string | null,
): string | undefined {
  return resolveStoredAvatarUrlWithCandidates(
    profile,
    USER_AVATAR_URL_FIELD_CANDIDATES,
    avatarBaseUrl,
  );
}

export function resolveStoredFoodCreatorAvatarUrl(
  profile?: Record<string, unknown> | null,
  avatarBaseUrl?: string | null,
): string | undefined {
  return resolveStoredAvatarUrlWithCandidates(
    profile,
    FOOD_CREATOR_AVATAR_URL_FIELD_CANDIDATES,
    avatarBaseUrl,
  );
}

function resolveStoredAvatarUrlWithCandidates(
  profile: Record<string, unknown> | null | undefined,
  candidates: readonly string[],
  avatarBaseUrl?: string | null,
): string | undefined {
  if (!profile) return undefined;

  let fallbackCribnoshAvatarId: CribnoshAvatarId | null = null;
  for (const field of candidates) {
    const candidate = typeof profile[field] === "string" ? profile[field].trim() : "";
    if (!candidate) continue;
    const avatarId = normalizeCribnoshAvatarId(candidate);
    if (avatarId) {
      fallbackCribnoshAvatarId ??= avatarId;
      continue;
    }
    return candidate;
  }

  const avatarValue = typeof profile.avatar === "string" ? profile.avatar.trim() : "";
  if (avatarValue) {
    const avatarId = normalizeCribnoshAvatarId(avatarValue);
    if (!avatarId) {
      return avatarValue;
    }
    fallbackCribnoshAvatarId ??= avatarId;
  }

  const cribnoshAvatarId =
    normalizeCribnoshAvatarId(
      firstNonEmpty([
        typeof profile.cribnoshAvatarId === "string" ? profile.cribnoshAvatarId : undefined,
        fallbackCribnoshAvatarId || undefined,
      ]),
    ) || fallbackCribnoshAvatarId;
  if (!cribnoshAvatarId) {
    return undefined;
  }

  return getCribnoshAvatarUrl(
    cribnoshAvatarId,
    avatarBaseUrl?.trim() || resolveCribnoshAvatarBaseUrl(process.env),
  );
}

export function resolveCribnoshAvatar(input: CribnoshAvatarResolutionInput): CribnoshAvatarResolution {
  const uploadedUri = input.profileImageUrl?.trim();
  if (input.avatarType === "uploaded" && uploadedUri) {
    return {
      kind: "uploaded",
      uri: uploadedUri,
      label: UPLOADED_AVATAR_LABEL,
    };
  }

  const selectedAvatarId = input.avatarType === "cribnosh"
    ? normalizeCribnoshAvatarId(input.cribnoshAvatarId)
    : null;

  if (selectedAvatarId) {
    return {
      kind: "cribnosh",
      uri: getCribnoshAvatarUrl(selectedAvatarId, input.avatarBaseUrl),
      avatarId: selectedAvatarId,
      label: getCribnoshAvatarLabel(selectedAvatarId),
    };
  }

  return {
    kind: "cribnosh",
    uri: getCribnoshDefaultAvatarUrl(input.avatarBaseUrl),
    label: CRIBNOSH_DEFAULT_AVATAR_LABEL,
  };
}
